import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import SvgFragment from "../SvgFragment";
import DropdownMenu from "../../components/dropdown/DropdownMenu";
import styled from "styled-components";
import { styles } from "../../styles/themes/style";
import DropdownItem from "../../components/dropdown/DropdownItem";
import SetTone from "./SetTone";
import Text from "../../components/Text";
import SetTranslate from "./SetTranslate";
import AiDropdownItem from "./components/AiDropdownItem";
import { useAssistantActions } from "../../stores/assistant/useAssistantActions";
import { useAssistantStore } from "../../stores/assistant/useAssistantStore";
import { useAssistantHandler } from "../../services/actions/assistant/useAssistantHandler";
import Button from "../../components/Button";
import LoadingSpinner from "../../components/LoadingSpinner";

interface IAiAssistantProps {
    content?: string
    chatPopup?: boolean
    modal?: boolean
    postCard?: boolean
    /** Inbox message bar: pill trigger with Sparkles + “Refine”, aligned with Templates / AI Generate */
    inboxPill?: boolean
    /** Sync with MessageInput ToolsRow ResizeObserver — icon-only when the toolbar row is narrow */
    inboxPillCompact?: boolean
    /** When true, Refine stays disabled (e.g. AI Generate in progress in the same composer). */
    refineExternallyDisabled?: boolean
    setContent?: (e: string) => void
    onDropdownToggle?: (isOpen: boolean) => void;
    onLoadingChange?: (isLoading: boolean) => void;
}

const Assistant: FunctionComponent<IAiAssistantProps> = ({content = '', setContent, onDropdownToggle, chatPopup, modal = false, onLoadingChange, postCard, inboxPill = false, inboxPillCompact = false, refineExternallyDisabled = false}) => {

    const { generateMessageHandler } = useAssistantHandler()
    const { setOriginalMessage, setFetchingAiMessage, setSparkleDropdown } = useAssistantActions()
    const { store: { originalMessage, fetchingAiMessage } } = useAssistantStore()

    const [dropdown, setDropdown] = useState<string>('')
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
    const toggleDropdown = (currentDropdown: string, target: string): string => currentDropdown === target ? '' : target

    const dropdownItems = [
        { handler: 'improve-grammar', label: 'Fix spelling', svg: 'spelling' },
        { handler: 'make-punchier', label: 'Make punchier', svg: 'punchier' },
        { handler: 'condense-message', label: 'Condense message', svg: 'condense' },
    ]

    useEffect(() => {
        if (content.trim().length < 5 && originalMessage) setOriginalMessage('')
    }, [content, originalMessage, setOriginalMessage])

    useEffect(() => {
        if(!isDropdownOpen) setDropdown('')
    }, [isDropdownOpen])

    const handleDropdownToggle = (isOpen: boolean) => {
        setIsDropdownOpen(isOpen)
        setSparkleDropdown(isOpen)
    } 
    
    const needsMoreChars = content.trim().length < 5
    const triggerDisabled = needsMoreChars || fetchingAiMessage || refineExternallyDisabled
    const disabledTooltip = 'To use this feature, you must enter a minimum of five characters.'

    const refineTooltip = useMemo(() => {
        if (!inboxPill) return 'Assistant'
        return 'Refine (Ctrl(⌘)+Shift+R)'
    }, [inboxPill])

    const refineDisabledTooltip = useMemo(() => {
        if (needsMoreChars) return disabledTooltip
        if (fetchingAiMessage) return 'Generating message…'
        if (refineExternallyDisabled) return 'Please wait…'
        return refineTooltip
    }, [needsMoreChars, fetchingAiMessage, refineExternallyDisabled, refineTooltip])

    useEffect(() => {
        if (fetchingAiMessage || refineExternallyDisabled) {
            setIsDropdownOpen(false)
            setSparkleDropdown(false)
            setDropdown('')
        }
    }, [fetchingAiMessage, refineExternallyDisabled, setSparkleDropdown])

    useEffect(() => {
        if (!inboxPill) return

        const onKeyDown = (e: KeyboardEvent) => {
            const key = String(e.key || '').toLowerCase()
            const isR = key === 'r'
            const combo = isR && e.shiftKey && (e.ctrlKey || e.metaKey)
            if (!combo) return

            // Ctrl+Shift+R is browser hard-reload; keep user in composer.
            e.preventDefault()
            e.stopPropagation()

            if (triggerDisabled) return
            handleDropdownToggle(true)
        }

        window.addEventListener('keydown', onKeyDown, { capture: true })
        return () => window.removeEventListener('keydown', onKeyDown, { capture: true } as any)
    }, [inboxPill, triggerDisabled])

    const generateMessage = async (action?: string, props?: any) => {
        setFetchingAiMessage(true)
        onLoadingChange?.(true)

        if (action === 'reset') {
            setContent?.(originalMessage)
            setFetchingAiMessage(false)
            onLoadingChange?.(false)
            return
        }
    
        setOriginalMessage(content)
    
        const commonData = { original_message: content }
        let payload = {}
    
        switch (action) {
            case 'change-tone':
                payload = {
                    type: 'change-tone',
                    data: { ...commonData, new_tone: props }
                }
                break
    
            case 'translate':
                payload = {
                    type: 'translate',
                    data: { ...commonData, new_language: props }
                }
                break
    
            default:
                if (dropdownItems.some((item: any) => item.handler === action)) {
                    payload = {
                        type: action,
                        data: commonData
                    }
                }
        }
    
        if (Object.keys(payload).length) {
            const newMessage = await generateMessageHandler(payload)
            if(newMessage) setContent?.(newMessage)
        }
    
        setFetchingAiMessage(false)
        onLoadingChange?.(false)
    }

    const refineAccent = '#6467f2'
    const refineMuted = '#8c96ad'
    const title = inboxPill ? (
        <InboxRefinePillTrigger $disabled={triggerDisabled} $compact={inboxPillCompact}>
            {fetchingAiMessage ? (
                <LoadingSpinner height="13px" width="13px" color={needsMoreChars ? refineMuted : refineAccent} />
            ) : (
                <Sparkles size={16} color={triggerDisabled ? refineMuted : refineAccent} strokeWidth={1.75} aria-hidden />
            )}
            <InboxRefinePillLabel $compact={inboxPillCompact}>Refine</InboxRefinePillLabel>
        </InboxRefinePillTrigger>
    ) : (chatPopup || postCard)
        ? <StyledSVG isDisabled={triggerDisabled}><SvgFragment type='sparkle'/></StyledSVG>
        : <Button className="ai-assistant-button" transparent isLoading={fetchingAiMessage} disabled={triggerDisabled} $SVGtype="aiAssistant"/>

    return (
        <StyledDropdownMenu
            svgDisableColor={triggerDisabled}
            disabled={triggerDisabled}
            isDisabled={triggerDisabled}
            tooltipMessage={triggerDisabled ? refineDisabledTooltip : refineTooltip}
            className="ai-assistant-dropdown"
            hideArrow
            onToggle={handleDropdownToggle}
            show={isDropdownOpen}
            title={title}
            $transparent={chatPopup || postCard || inboxPill}
            $chatPopup={chatPopup}
            $modal={modal}
            $postCard={postCard}
            $inboxPill={inboxPill}
            svgPath
            {...(inboxPill ? { drop: 'up' as const } : {})}
        >
            <DropdownItem $disabled><Text $heading5>Refine the message</Text></DropdownItem>
            <SetTone
                isDisabled={triggerDisabled}
                generateMessage={generateMessage}
                dropdown={dropdown}
                setDropdown={() => triggerDisabled ? undefined : setDropdown((prev) => toggleDropdown(prev, 'tone'))}
            />
            <SetTranslate
                isDisabled={triggerDisabled}
                generateMessage={generateMessage} 
                dropdown={dropdown}
                setDropdown={() => triggerDisabled ? undefined : setDropdown((prev) => toggleDropdown(prev, 'translate'))}
            />
            {dropdownItems.map(({ handler, label, svg }) => (
                <DropdownItem $disabled={triggerDisabled} onClickHandler={() => generateMessage(handler, null)}>
                    <AiDropdownItem isDisabled={triggerDisabled} padding="5px 0" key={handler} label={label} svg={svg}/>
                </DropdownItem>
            ))}
            <Separator />
            <DropdownItem $disabled={triggerDisabled} onClickHandler={() => generateMessage('reset', null)}>
                <AiDropdownItem isDisabled={triggerDisabled} padding="5px 0" label="Reset to original message" svg="undo"/>
            </DropdownItem>
        </StyledDropdownMenu>
    )
}

const StyledDropdownMenu = styled(DropdownMenu)<{$chatPopup?: boolean, $modal?: boolean, isDisabled?: boolean, $postCard?: boolean, $inboxPill?: boolean}>`
    ${({ $inboxPill, $chatPopup, isDisabled, $postCard }) => $inboxPill ? `
        width: auto;
        height: auto;
        background: transparent !important;
        & .ai-assistant-dropdown {
            width: auto !important;
            height: auto !important;
            min-height: unset !important;
            padding: 0 !important;
            background: transparent !important;
        }
        & .dropdown-toggle {
            padding: 0 !important;
            min-height: unset !important;
        }
        & .dropdown-toggle:not(:disabled):hover [data-inbox-refine-pill] {
            background: rgba(100, 103, 242, 0.08);
        }
    ` : ($chatPopup || $postCard) ? `
        width: 20px;
        height: 20px;
        background: transparent !important;
    ` : `
        & .ai-assistant-dropdown {
            width: 42px;
            height: 42px;
            background-color: ${styles.colors.primary100} !important;

            ${!isDisabled && `
                &:hover {
                    border: 1px solid ${styles.colors.primary600} !important;
                }
            `}
        }

        & .ai-assistant-button:hover {
            background-color: transparent !important;
        }
    `}

    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 5px;

    & .actions-dropdown + div  {
        ${({$inboxPill, $chatPopup, $modal, $postCard}) => $inboxPill ? `
            width: 240px;
            z-index: 10050 !important;
        ` : $chatPopup ? `
            width: 240px;
            transform: translate3d(0px, -25px, 0px) !important;
        ` : $modal ? `
            transform: translate3d(0px, 50px, 0px) !important;
            width: 350px;
        ` : $postCard ? `
            width: 240px;
        ` : `
            width: 350px;
        `}
        overflow-x: hidden;
        max-height: 600px;
    }
`

const iconOnlyPill = `
    width: 22px;
    height: 22px;
    padding: 0;
    justify-content: center;
    gap: 0;
`

const InboxRefinePillTrigger = styled.span.attrs({ 'data-inbox-refine-pill': true })<{ $disabled?: boolean; $compact?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 9999px;
    color: ${({ $disabled }) => ($disabled ? '#8c96ad' : '#6467f2')};
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
    transition: background 0.12s, opacity 0.12s, color 0.12s;
    opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'inherit')};

    ${({ $compact }) => $compact && iconOnlyPill}

    /* Match MessageInput PillBtn */
    @media (max-width: 1280px) {
        ${iconOnlyPill}
    }
`

const InboxRefinePillLabel = styled.span<{ $compact?: boolean }>`
    ${({ $compact }) => $compact && `display: none;`}

    @media (max-width: 1280px) {
        display: none;
    }
`

const Separator = styled.div`
    width: 350px;
    height: 2px;
    margin: 4px 0;
    background-color: #EBEFF4 !important;
`

const StyledSVG = styled.div<{isDisabled: boolean}>`
    cursor: pointer;

    width: 20px;
    height: 20px;

    & svg {
        width: 20px;
        height: 20px;
    }

    &:hover svg {
        path {
            stroke: ${styles.colors.primary600};
            ${({isDisabled}) => !isDisabled && `fill: ${styles.colors.primary600};`}
        }
    }
`;
export default Assistant